import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { GameLoadingScreen } from './GameLoadingScreen'
import {
  getRouteAssetUrls,
  routeAssetWarmKey,
  type RouteAssetArea,
} from '../constants/routeAssetManifests'
import { AREA_FIRST_MESSAGE } from '../constants/loadingMessages'
import {
  auditBlobWarmCache,
  clearBootstrapBlobWarmComplete,
  isBootstrapBlobWarmComplete,
  preloadBlobUrlsWithProgress,
} from '../utils/assetWarmup'
import type { AssetWarmEvent } from '../utils/assetWarmup'
import { isLoadDebugEnabled } from '../utils/vercelAssetPreloadGate'
import type { GatePreloadDetail } from '../utils/vercelAssetPreloadGate'
import { useLoadingProgressView } from '../utils/loadingProgressDisplay'

/**
 * Blocks a destination until its artwork is downloaded, behind the game's loading screen.
 *
 * The rule this enforces: a screen is either fully drawn or not shown at all. Partially-loaded
 * pages that fill in under the player read as a broken build, and no amount of background warming
 * makes that impossible — it only makes it rarer.
 *
 * "Already warm" is verified, not assumed. The persisted per-area flag only records that a warm
 * once finished; storage eviction, or a hard reload that loads the page past the service worker,
 * leaves the flag set while nothing the page paints is actually cached. So the first entry to an
 * area in a page load audits Cache Storage (a key scan, tens of milliseconds) and re-runs the
 * warm — fetching only what is missing — when the audit fails. Once an area has been verified or
 * warmed in this page load, re-entering it mounts synchronously with no loading screen at all.
 */

/** Never leave a player stranded on a loading screen because one asset will not resolve. */
const ROUTE_GATE_SAFETY_MAX_MS = 90 * 1000

/** Higher than the background sweep: the player is watching this bar and nothing competes. */
const ROUTE_GATE_CONCURRENCY = 12

type Props = {
  area: RouteAssetArea
  children: React.ReactNode
}

function isAreaWarm(area: RouteAssetArea): boolean {
  if (typeof window === 'undefined') return true
  if (getRouteAssetUrls(area).length === 0) return true
  return isBootstrapBlobWarmComplete(routeAssetWarmKey(area))
}

type AreaWarmRun = {
  promise: Promise<void>
  loaded: number
  total: number
  lastEvent: AssetWarmEvent | null
  subscribers: Set<() => void>
}

/**
 * One warm per area for the lifetime of the page, tracked outside React.
 *
 * The component alone cannot own this. A player who backs out of a loading destination and returns
 * remounts the gate, and a component-local guard then either restarts a download that is already
 * running or — worse — refuses to restart and leaves the bar frozen at 0% with nothing driving it.
 * Keying the run to the area instead means re-entry attaches to the work already in flight and
 * inherits its progress.
 */
const areaRuns = new Map<RouteAssetArea, AreaWarmRun>()

/** Areas proven ready in this page load — by a passed audit or a finished warm. Synchronous re-entry. */
const verifiedAreas = new Set<RouteAssetArea>()

/** In-flight audits, shared so a remount during the scan attaches instead of scanning twice. */
const areaAudits = new Map<RouteAssetArea, Promise<boolean>>()

function isAreaVerified(area: RouteAssetArea): boolean {
  if (typeof window === 'undefined') return true
  if (getRouteAssetUrls(area).length === 0) return true
  return verifiedAreas.has(area)
}

/**
 * Whether the persisted flag for this area holds for THIS page load. Resolves `false` when a warm
 * must run: no flag, a warm already in flight (join it), or an audit that found unreachable assets
 * (the flag is dropped so the warm does not short-circuit on it). An audit that cannot run at all
 * trusts the flag, as before — "could not verify" must not become a loading screen on every visit.
 */
function verifyAreaWarm(area: RouteAssetArea): Promise<boolean> {
  if (isAreaVerified(area)) return Promise.resolve(true)
  const pending = areaAudits.get(area)
  if (pending) return pending
  if (areaRuns.has(area)) return Promise.resolve(false)
  const key = routeAssetWarmKey(area)
  if (!isBootstrapBlobWarmComplete(key)) return Promise.resolve(false)

  const audit = (async () => {
    try {
      const result = await auditBlobWarmCache(getRouteAssetUrls(area), key)
      if (result.missing === 0) {
        verifiedAreas.add(area)
        return true
      }
      /**
       * Same rule as the startup gate: an audit that could not read Cache Storage has not proved
       * anything, so the persisted flag stands and is NOT cleared. Clearing on an unverified miss
       * is self-perpetuating — the cleared flag makes the next visit cold, which re-runs the warm,
       * which clears it again, so the area's loading screen appears on every single entry.
       */
      if (!result.verified) {
        console.warn(
          `[route-gate] ${area}: audit could not read Cache Storage — trusting the persisted warm flag`,
        )
        verifiedAreas.add(area)
        return true
      }
      console.info(
        `[route-gate] ${area}: ${result.missing}/${result.total} assets missing from Cache Storage` +
          (result.swControlled ? '' : ' (page is not controlled by the service worker)') +
          ' — re-running the warm behind the loading screen',
      )
      clearBootstrapBlobWarmComplete(key)
      return false
    } catch (e) {
      console.warn(`[route-gate] ${area}: audit could not run; trusting the persisted warm flag`, e)
      verifiedAreas.add(area)
      return true
    } finally {
      areaAudits.delete(area)
    }
  })()
  areaAudits.set(area, audit)
  return audit
}

function startOrJoinAreaWarm(area: RouteAssetArea): AreaWarmRun {
  const existing = areaRuns.get(area)
  if (existing) return existing

  const urls = [...getRouteAssetUrls(area)]
  const run: AreaWarmRun = {
    promise: Promise.resolve(),
    loaded: 0,
    total: urls.length,
    lastEvent: null,
    subscribers: new Set(),
  }
  const emit = () => {
    run.subscribers.forEach((fn) => {
      try {
        fn()
      } catch {
        /* a broken subscriber must not stop the warm */
      }
    })
  }

  run.promise = preloadBlobUrlsWithProgress(
    urls,
    (l, t) => {
      run.loaded = l
      run.total = t
      emit()
    },
    ROUTE_GATE_CONCURRENCY,
    routeAssetWarmKey(area),
    {
      // One dead asset must not keep a destination permanently unreachable.
      allowPartialComplete: true,
      // Same reasoning as the cold gate: the bytes are what matter, and the browser decodes again
      // at paint anyway.
      decodeImages: false,
      onAssetEvent: (e) => {
        run.lastEvent = e
        emit()
      },
    },
  )
    .catch(() => {
      /* already logged inside the warm; the player still gets in */
    })
    .finally(() => {
      // The warm ran in this page load, so the area is proven for the rest of it. Leave the run
      // entry in place too: a late remount then resolves immediately instead of starting a
      // second pass.
      verifiedAreas.add(area)
      emit()
    })

  areaRuns.set(area, run)
  return run
}

type GateStatus = 'verifying' | 'warming' | 'ready'

type GateState = {
  /** The area this state was computed for; a stale area renders as verifying until the effect syncs. */
  area: RouteAssetArea
  status: GateStatus
  /** No warm ran for this area in this mount, so there is nothing to hold a 100% frame for. */
  skipped: boolean
}

function initialGateState(area: RouteAssetArea): GateState {
  const ready = isAreaVerified(area)
  return { area, status: ready ? 'ready' : 'verifying', skipped: ready }
}

export const RouteAssetGate: React.FC<Props> = ({ area, children }) => {
  // Synchronous initial state — an area verified earlier in this page load never renders the
  // loading screen at all.
  const [state, setState] = useState<GateState>(() => initialGateState(area))
  const [progress, setProgress] = useState({ loaded: 0, total: 0 })
  const [detail, setDetail] = useState<GatePreloadDetail | null>(null)
  const [debug] = useState(isLoadDebugEnabled)

  useEffect(() => {
    if (isAreaVerified(area)) {
      setState({ area, status: 'ready', skipped: true })
      return
    }
    setState({ area, status: 'verifying', skipped: false })
    setProgress({ loaded: 0, total: 0 })
    setDetail(null)

    let cancelled = false
    let run: AreaWarmRun | null = null
    let sync: (() => void) | null = null
    let safety = 0

    const warm = () => {
      run = startOrJoinAreaWarm(area)
      const current = run
      setState({ area, status: 'warming', skipped: false })

      sync = () => {
        if (cancelled) return
        setProgress({ loaded: current.loaded, total: current.total })
        if (debug && current.lastEvent) {
          const e = current.lastEvent
          setDetail({
            group: e.url.split('/').slice(-2, -1)[0] ?? '',
            name: (e.url.split('/').pop() ?? '').replace(/\.[a-z0-9]+$/i, ''),
            inFlight: e.inFlight,
            slowest: [],
          })
        }
      }
      current.subscribers.add(sync)
      sync()

      safety = window.setTimeout(() => {
        if (!cancelled) setState({ area, status: 'ready', skipped: false })
      }, ROUTE_GATE_SAFETY_MAX_MS)

      void current.promise.finally(() => {
        if (cancelled) return
        window.clearTimeout(safety)
        setState({ area, status: 'ready', skipped: false })
      })
    }

    void verifyAreaWarm(area).then((warmAlready) => {
      if (cancelled) return
      if (warmAlready) setState({ area, status: 'ready', skipped: true })
      else warm()
    })

    return () => {
      cancelled = true
      window.clearTimeout(safety)
      if (run && sync) run.subscribers.delete(sync)
      // Deliberately does NOT abort the warm: the player is probably heading back here, and the
      // fetches already in flight are worth finishing into Cache Storage.
    }
  }, [area, debug])

  // State computed for a previous area must not show this one's children for a frame.
  const current = state.area === area ? state : initialGateState(area)

  return (
    <RouteAssetGateView
      area={area}
      loaded={progress.loaded}
      total={progress.total}
      verifying={current.status === 'verifying'}
      finished={current.status === 'ready'}
      skipped={current.skipped}
      detail={detail}
      debug={debug}
    >
      {children}
    </RouteAssetGateView>
  )
}

/**
 * Split out so the progress hook runs unconditionally (hooks cannot sit behind the early return
 * that swaps the screen for the page).
 */
const RouteAssetGateView: React.FC<{
  area: RouteAssetArea
  loaded: number
  total: number
  verifying: boolean
  finished: boolean
  skipped: boolean
  detail: GatePreloadDetail | null
  debug: boolean
  children: React.ReactNode
}> = ({ area, loaded, total, verifying, finished, skipped, detail, debug, children }) => {
  // Keyed by area so a second destination starts from an empty bar instead of inheriting the fill
  // of the one before it (this component instance is reused across navigations).
  const { pct, visible } = useLoadingProgressView(loaded, total, finished, area)

  // Nothing while the audit runs: mounting the destination would start the on-demand fetches the
  // gate exists to prevent, and a loading screen for a scan that usually takes tens of
  // milliseconds would flash on every healthy navigation.
  if (verifying) return null

  // A verified-warm area shows no screen at all: the closing beat is for a bar the player watched,
  // and adding half a second to an instant navigation would be worse than the pop-in it prevents.
  if (skipped || !visible) return <>{children}</>

  return (
    <GameLoadingScreen
      variant="compact"
      pct={pct}
      total={total}
      detail={detail}
      firstMessage={AREA_FIRST_MESSAGE[area]}
      debug={debug}
    />
  )
}

/** Convenience wrapper for route elements: `withRouteAssets('kingdom', <KingdomPage />)`. */
export function withRouteAssets(area: RouteAssetArea, element: React.ReactNode): React.ReactElement {
  return <RouteAssetGate area={area}>{element}</RouteAssetGate>
}

/**
 * Which destination a path belongs to. First match wins, so more specific prefixes come first —
 * `/kingdom/alliance` is an alliance screen, not a kingdom one, and `/kingdom/mvm-captives` is
 * kingdom rather than MvM despite the name.
 */
const PATH_AREAS: ReadonlyArray<{ test: (p: string) => boolean; area: RouteAssetArea }> = [
  { test: (p) => p.startsWith('/kingdom/alliance'), area: 'alliance' },
  { test: (p) => p.startsWith('/alliance'), area: 'alliance' },
  { test: (p) => p.startsWith('/kingdom'), area: 'kingdom' },
  { test: (p) => p.startsWith('/world-map'), area: 'world-map' },
  { test: (p) => p.startsWith('/hex-battle'), area: 'mvm' },
  { test: (p) => p.startsWith('/eclipsera-bonds'), area: 'minigame' },
  { test: (p) => p.startsWith('/pve'), area: 'pve' },
  { test: (p) => p.startsWith('/battlefield') || p.startsWith('/battle-preview'), area: 'pve' },
  { test: (p) => p.startsWith('/tournament'), area: 'pvp' },
  { test: (p) => p.startsWith('/pvp'), area: 'pvp' },
  { test: (p) => p.startsWith('/leaderboard'), area: 'pvp' },
]

export function routeAreaForPath(pathname: string): RouteAssetArea | null {
  const p = pathname.toLowerCase()
  for (const { test, area } of PATH_AREAS) {
    if (test(p)) return area
  }
  return null
}

/**
 * Gates by URL instead of per-`<Route>`.
 *
 * Wrapping every route element individually would mean ~25 near-identical edits and a standing
 * invitation to forget one when a route is added. One path map keeps the policy in a single
 * readable place. Paths with no area render straight through.
 */
export const RouteAssetGateByPath: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { pathname } = useLocation()
  const area = routeAreaForPath(pathname)
  if (!area) return <>{children}</>
  return <RouteAssetGate area={area}>{children}</RouteAssetGate>
}
